import * as React from 'react';
import type { OptionProps } from 'react-apollo';
import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';
import { apolloFetchPolicy } from 'js/utils/apolloFetchPolicy';

import type { ProfessionalCertificatesData } from 'bundles/browse/components/__generated__/ProfessionalCertificatesData';
import type {
  ProfessionalCertificateElement,
  ProfessionalCertificateProps,
  WithProfessionalCertificatesProps,
} from 'bundles/browse/components/types/ProfessionalCertificate';
import { buildProfessionalCertificateUrlAbsolute, getXdpSubfolderPrefixPath } from 'bundles/common/utils/urlUtils';

type ProfessionalCertificateDataResult = {
  certs: (ProfessionalCertificateElement | null)[];
  professionalCertificatesLoading?: boolean;
};

export const ProfessionalCertificatesDataQuery = gql`
  query ProfessionalCertificatesData {
    ProfessionalCertsV1Resource {
      getAll(limit: 200) {
        elements {
          id
          name
          slug
          tagline
          partnerSquareLogo
          thumbnail
          partnerIds
        }
      }
    }
  }
`;

const withProfessionalCertificates =
  () =>
  <T extends {}>(BaseComponent: React.ComponentType<T>) => {
    const componentName = BaseComponent.displayName || BaseComponent.name;

    class HOC extends React.Component<T & WithProfessionalCertificatesProps> {
      static displayName = `withProfessionalCertificates(${componentName})`;

      render() {
        return <BaseComponent {...this.props} />;
      }
    }

    hoistNonReactStatics(HOC, BaseComponent);

    return compose<T & WithProfessionalCertificatesProps, T>(
      graphql(ProfessionalCertificatesDataQuery, {
        options: () => ({
          ...apolloFetchPolicy({ cacheForLoggedOutOnly: false }),
          ssr: false,
        }),
        props: ({ data }: OptionProps<T, ProfessionalCertificatesData, T>): ProfessionalCertificateDataResult => {
          const isLoading = data?.loading;
          return {
            certs: data?.ProfessionalCertsV1Resource?.getAll?.elements || [],
            professionalCertificatesLoading: isLoading,
          };
        },
      }),
      connectToRouter((router) => ({ pathname: router.location.pathname })),
      withProps(
        ({
          certs,
          professionalCertificatesLoading,
          pathname,
        }: ProfessionalCertificateDataResult & { pathname: string }): WithProfessionalCertificatesProps => {
          const professionalCertificates = certs
            .map((cert) => {
              if (!cert) return null;
              return {
                ...cert,
                link: buildProfessionalCertificateUrlAbsolute(cert.slug, '', getXdpSubfolderPrefixPath(pathname)),
              };
            })
            .filter((cert): cert is ProfessionalCertificateProps => cert !== null);
          return {
            professionalCertificates,
            professionalCertificatesLoading,
          };
        }
      )
    )(HOC);
  };

export function useProfessionalCertificates(): WithProfessionalCertificatesProps {
  const { data, loading } = useQuery<ProfessionalCertificatesData>(ProfessionalCertificatesDataQuery, {
    ...apolloFetchPolicy({ cacheForLoggedOutOnly: false }),
  });

  const certs = data?.ProfessionalCertsV1Resource?.getAll?.elements || [];

  const professionalCertificates = certs
    .map((cert) => {
      if (!cert) return null;
      return { ...cert, link: buildProfessionalCertificateUrlAbsolute(cert.slug) };
    })
    .filter((cert): cert is ProfessionalCertificateProps => cert !== null);

  return {
    professionalCertificates,
    professionalCertificatesLoading: loading,
  };
}

export default withProfessionalCertificates;
